// Blended global search overlay. One query returns BOTH matching device manuals
// (openable rows) and matching devices (rows that link to detail). Desktop: a
// centered dropdown; mobile: a full-screen sheet (per dashboard.css). Runs
// entirely client-side over the already-fetched inventory.

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatBytes } from '../../lib/deviceSeen';
import { searchInventory } from '../../lib/searchInventory';
import type { VesselDevice } from '../../lib/useBoatDevices';
import { DevicesTabIcon, DocIcon, SearchIcon } from '../app/icons';

const SUGGESTIONS = ['autopilot', 'depth', 'battery', 'AIS'];

export function SearchOverlay({
  open,
  ownInput = false,
  query,
  devices,
  boatId,
  onQuery,
  onClose,
}: {
  open: boolean;
  /** Desktop: show the overlay's own input. The boat shell always does: its search is a sidebar row (TOM-513). */
  ownInput?: boolean;
  query: string;
  devices: VesselDevice[];
  boatId: string;
  onQuery: (q: string) => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 30);
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const q = query.trim();
  const { manuals, devices: deviceHits } = searchInventory(devices, query);

  function openDevice(d: VesselDevice) {
    onClose();
    navigate(`/dashboard/boats/${boatId}/devices/${encodeURIComponent(d.deviceId)}`);
  }

  return (
    <>
      <div className={`scrim${open ? ' open' : ''}`} onClick={onClose} />
      <div
        className={`search-overlay${open ? ' open' : ''}${ownInput ? ' search-overlay--own-input' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Search results"
        data-testid="search-overlay"
      >
        <div className="msearch-header">
          <div className="search-box">
            <SearchIcon className="search-ico" />
            <input
              ref={inputRef}
              type="search"
              value={query}
              placeholder="Find a device manual…"
              autoComplete="off"
              aria-label="Find a device manual"
              data-testid="manual-search"
              onChange={(e) => onQuery(e.target.value)}
            />
          </div>
          <button type="button" className="msearch-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>

        <div className="search-overlay-inner" data-testid="search-results">
          {q === '' ? (
            <div className="search-recent">
              <div className="recent-label">Try a role or a brand</div>
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="recent-chip" onClick={() => onQuery(s)}>
                  <SearchIcon />
                  {s}
                </button>
              ))}
            </div>
          ) : manuals.length === 0 && deviceHits.length === 0 ? (
            <div className="search-empty">
              No device or manual matches “{q}”.
              <br />
              <span className="muted">
                Try a role like <strong>autopilot</strong> or <strong>depth</strong>, or a
                manufacturer.
              </span>
            </div>
          ) : (
            <>
              {manuals.length > 0 ? (
                <>
                  <div className="search-group-label">
                    Manuals <span className="cnt">{manuals.length}</span>
                  </div>
                  {manuals.map(({ manual: m, device: d }) => {
                    const size = m.sizeBytes != null ? formatBytes(m.sizeBytes) : '';
                    const sub = [d.preferredName ?? d.displayName, m.revision, m.language, size]
                      .filter(Boolean)
                      .join(' · ');
                    return (
                      <a
                        key={m.id}
                        className="sr-row"
                        href={m.downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={onClose}
                        data-testid={`search-manual-${m.id}`}
                      >
                        <div className="sr-doc">
                          <DocIcon />
                        </div>
                        <div className="sr-main">
                          <div className="sr-title">{m.title}</div>
                          <div className="sr-sub">{sub}</div>
                        </div>
                        <span className="sr-open">Open</span>
                      </a>
                    );
                  })}
                </>
              ) : null}

              {deviceHits.length > 0 ? (
                <>
                  <div className="search-group-label">
                    Devices <span className="cnt">{deviceHits.length}</span>
                  </div>
                  {deviceHits.map((d) => {
                    const count = d.manuals?.length ?? 0;
                    const role = d.deviceFunction?.name ?? d.deviceClass?.name ?? '';
                    const sub = [
                      count > 0 ? `${count} manual${count === 1 ? '' : 's'}` : 'No manual on file',
                      role,
                    ]
                      .filter(Boolean)
                      .join(' · ');
                    return (
                      <button
                        key={d.deviceId}
                        type="button"
                        className="sr-row"
                        onClick={() => openDevice(d)}
                        data-testid={`search-device-${d.deviceId}`}
                      >
                        <div className="sr-doc sr-doc--device">
                          <DevicesTabIcon />
                        </div>
                        <div className="sr-main">
                          <div className="sr-title">
                            {d.displayName}{' '}
                            <span className="sr-model">
                              · {d.manufacturer?.name ?? ''} {d.productInfo?.modelId ?? ''}
                            </span>
                          </div>
                          <div className="sr-sub">{sub}</div>
                        </div>
                        <span className="sr-open">{count > 0 ? 'Open manual' : 'View'}</span>
                      </button>
                    );
                  })}
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </>
  );
}
